// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
import './add_jquery'
import select2Factory from "select2/dist/js/select2.full"
import * as bootstrap from "bootstrap"
import "./channels"
import "./lib/case_timeline"
import { initializeBatchIngestForms } from "./lib/batch_ingest_status"

select2Factory(window, window.jQuery);
window.bootstrap = bootstrap;

if (window.Turbo) {
    window.Turbo.config?.drive && (window.Turbo.config.drive.progressBarDelay = 2147483647);
}

function turboStreamJsonPayload(streamElement) {
    const text = streamElement.templateContent?.textContent || "{}";
    try {
        return JSON.parse(text);
    } catch (error) {
        console.warn("Invalid Turbo stream payload", error);
        return {};
    }
}

const CASE_ANALYSIS_FORBIDDEN_LIVE_TARGET_IDS = new Set([
    "case-analysis-live-region",
    "case-document-analysis-right-column",
    "case-document-analysis-response",
    "case-document-analysis-progress-stats"
]);

const CASE_ANALYSIS_PROTECTED_LIVE_DESCENDANTS = [
    "[data-case-analysis-rendered]",
    "[data-case-analysis-thinking]",
    "[data-case-analysis-source-panel]",
    "[data-case-analysis-source-table]",
    "[data-case-analysis-source-list-body]",
    "#case-document-analysis-response-stream"
].join(", ");

function caseAnalysisLiveTargetAllowed(element) {
    if (!element) return false;
    // Header source counts must always accept live turbo text updates.
    if (element.hasAttribute?.("data-case-analysis-source-count")) return true;
    if (String(element.id || "").match(/^case-document-analysis-\d+-source-count-/)) return true;
    if (CASE_ANALYSIS_FORBIDDEN_LIVE_TARGET_IDS.has(element.id)) return false;
    return !element.querySelector?.(CASE_ANALYSIS_PROTECTED_LIVE_DESCENDANTS);
}

function syncCaseAnalysisSourceHeaderCountsFromList(listRoot) {
    if (!listRoot) return;

    const analysisId = String(
        listRoot.dataset.caseDocumentAnalysisId ||
        listRoot.querySelector?.("[data-case-document-analysis-id]")?.dataset?.caseDocumentAnalysisId ||
        ""
    );
    const body = listRoot.querySelector?.("[data-case-analysis-source-list-body='true']") || listRoot;
    const items = Array.from(body.querySelectorAll?.(".case-analysis-source-item") || []);
    // The progress snapshot supplies provisional counts until rows are ready.
    if (items.length === 0) return;
    let docs = 0;
    let authorities = 0;

    items.forEach((item) => {
        const category = String(item.querySelector?.("[data-source-category]")?.textContent || "")
            .trim()
            .toLowerCase();
        if (
            category.includes("legal") ||
            category.includes("case law") ||
            category.includes("authority") ||
            category.includes("code")
        ) {
            authorities += 1;
        } else {
            docs += 1;
        }
    });

    const applyCount = (kind, value) => {
        // Always set absolute list-derived counts (source of truth for the sidebar).
        const selectors = [
            analysisId ? `#case-document-analysis-${analysisId}-source-count-${kind}` : null,
            analysisId
                ? `[data-case-analysis-source-count="${kind}"][data-case-document-analysis-id="${analysisId}"]`
                : null,
            `[data-case-analysis-source-count="${kind}"]`
        ].filter(Boolean);

        const seen = new Set();
        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((element) => {
                if (seen.has(element)) return;
                seen.add(element);
                const next = String(value);
                if (element.textContent !== next) element.textContent = next;
                if (analysisId && !element.dataset.caseDocumentAnalysisId) {
                    element.dataset.caseDocumentAnalysisId = analysisId;
                }
            });
        });
    };

    applyCount("case-document", docs);
    applyCount("legal-authority", authorities);
}

function caseAnalysisSourceListUpdateAllowed(streamElement, target) {
    if (!target || target.id !== "case-document-analysis-source-list") return true;

    const currentAnalysisId = String(target.dataset.caseDocumentAnalysisId || "");
    const incomingAnalysisId = String(
        streamElement.templateContent?.firstElementChild?.dataset?.caseDocumentAnalysisId || ""
    );

    return currentAnalysisId === "" || incomingAnalysisId === "" || currentAnalysisId === incomingAnalysisId;
}

function syncCaseAnalysisSourceListTarget(streamElement, target) {
    if (!target || target.id !== "case-document-analysis-source-list") return;
    const incomingAnalysisId = String(
        streamElement.templateContent?.firstElementChild?.dataset?.caseDocumentAnalysisId || ""
    );
    if (incomingAnalysisId) target.dataset.caseDocumentAnalysisId = incomingAnalysisId;
    // After list HTML is swapped, re-sync the "Sources N docs · M authorities" header.
    queueMicrotask(() => syncCaseAnalysisSourceHeaderCountsFromList(target));
}

function clampCaseAnalysisProgressPercent(value) {
    const percent = Number.parseInt(value || 0, 10);
    if (Number.isNaN(percent)) return 0;
    return Math.max(0, Math.min(percent, 100));
}

function currentCaseAnalysisProgressPercent(element) {
    const progressState = caseAnalysisProgressState();
    const cacheKey = caseAnalysisProgressCacheKeyForElement(element);
    return Math.max(
        clampCaseAnalysisProgressPercent(progressState.get(cacheKey)),
        clampCaseAnalysisProgressPercent(element.dataset.caseAnalysisProgressValue),
        clampCaseAnalysisProgressPercent(element.getAttribute("aria-valuenow"))
    );
}

const STRATEGY_PIPELINE_PHASES = new Set(["ledger", "outline", "writing", "assembling", "validating", "complete", "failed"]);

function strategyPipelinePhase(phase) {
    return STRATEGY_PIPELINE_PHASES.has(String(phase || ""));
}

function nextCaseAnalysisProgressPercent(element, value, force = false, phase = null) {
    const requested = clampCaseAnalysisProgressPercent(value);
    const percent = force || strategyPipelinePhase(phase)
        ? requested
        : Math.max(currentCaseAnalysisProgressPercent(element), requested);
    caseAnalysisProgressState().set(caseAnalysisProgressCacheKeyForElement(element), percent);
    return percent;
}

function caseAnalysisProgressState() {
    window.caseAnalysisProgressValues ||= new Map();
    return window.caseAnalysisProgressValues;
}

function caseAnalysisProgressCacheKeyForElement(element) {
    const match = String(element?.id || "").match(/^case-document-analysis-(\d+)-progress-progress-track$/);
    return match ? `${match[1]}:progress-track` : (element?.id || "case-analysis-progress-track");
}

function caseAnalysisProgressTrackTargetsForPercentElement(element) {
    const match = String(element?.id || "").match(/^case-document-analysis-(\d+)-progress-progress-percent$/);
    if (!match) return [];

    const scope = element.closest("[data-case-analysis-detail]") ||
        element.closest("#case-analysis-live-region") ||
        document;
    const selector = `#${CSS.escape(`case-document-analysis-${match[1]}-progress-progress-track`)}`;
    return Array.from(scope.querySelectorAll(selector));
}

function setCaseAnalysisProgressTrackElement(element, value, force = false, phase = null) {
    if (phase) element.dataset.caseAnalysisProgressPhase = String(phase);
    const percent = nextCaseAnalysisProgressPercent(element, value, force, phase);
    const percentText = String(percent);
    const width = `${percent}%`;

    element.dataset.caseAnalysisProgressValue = percentText;
    element.removeAttribute("data-progress-width");
    element.style.setProperty("--case-analysis-progress-percent", width);
    element.classList.toggle("case-analysis-progress-track--complete", percent >= 100);
    if (element.getAttribute("aria-valuenow") !== percentText) {
        element.setAttribute("aria-valuenow", percentText);
    }

    let fill = element.querySelector(".progress-bar");
    if (!fill) {
        fill = document.createElement("div");
        fill.className = "progress-bar";
        element.appendChild(fill);
    }

    if (!fill.classList.contains("progress-bar")) fill.classList.add("progress-bar");
    if (fill.style.width !== width) fill.style.width = width;
}

function syncCaseAnalysisProgressTrackForPercentElement(element, value, force = false, phase = null) {
    const fromElement = phase || element?.dataset?.caseAnalysisProgressPhase || null;
    caseAnalysisProgressTrackTargetsForPercentElement(element).forEach((track) => {
        const resolved = fromElement || track?.dataset?.caseAnalysisProgressPhase || null;
        if (caseAnalysisLiveTargetAllowed(track)) setCaseAnalysisProgressTrackElement(track, value, force, resolved);
    });
}

function syncCaseAnalysisProgressTracks(root = document) {
    const scope = root instanceof Element ? root : document;
    const percentTargets = [];

    if (scope.matches?.('[id^="case-document-analysis-"][id$="-progress-progress-percent"]')) {
        percentTargets.push(scope);
    }
    percentTargets.push(
        ...Array.from(scope.querySelectorAll?.('[id^="case-document-analysis-"][id$="-progress-progress-percent"]') || [])
    );

    percentTargets.forEach((element) => {
        syncCaseAnalysisProgressTrackForPercentElement(
            element,
            element.textContent,
            false,
            element.dataset?.caseAnalysisProgressPhase || null
        );
    });
}

// SHARED ANALYZE MODAL: one modal serves every strategy row. The trigger
// button carries the strategy's analyze URL and title; on show, the modal's
// form is retargeted to that strategy.
document.addEventListener("show.bs.modal", (event) => {
    if (event.target?.id !== "case-strategy-analyze-modal") return;
    const trigger = event.relatedTarget;
    if (!trigger) return;
    const form = event.target.querySelector("[data-case-strategy-analyze-modal-form]");
    if (form && trigger.dataset.analyzeUrl) form.setAttribute("action", trigger.dataset.analyzeUrl);
    const title = event.target.querySelector("[data-case-strategy-analyze-modal-strategy-title]");
    if (title) title.textContent = trigger.dataset.strategyTitle || "";
});

if (window.Turbo?.StreamActions) {
    window.Turbo.StreamActions.close_modal = function () {
        this.targetElements.forEach((element) => {
            if (!window.bootstrap) return;
            window.bootstrap.Modal.getInstance(element)?.hide();
        });
    };

    window.Turbo.StreamActions.case_analysis_text = function () {
        const payload = turboStreamJsonPayload(this);

        this.targetElements.forEach((element) => {
            if (!caseAnalysisLiveTargetAllowed(element)) return;

            let nextText = payload.text == null ? "" : String(payload.text);
            // Source header counts: never drop on a lower live snapshot unless forced.
            if (element.hasAttribute?.("data-case-analysis-source-count") && payload.force !== true) {
                const next = Number.parseInt(nextText, 10);
                const current = Number.parseInt(element.textContent || "0", 10);
                if (!Number.isNaN(next) && !Number.isNaN(current) && next < current) {
                    nextText = String(current);
                }
            }
            if (element.textContent !== nextText) element.textContent = nextText;
            if (payload.phase) element.dataset.caseAnalysisProgressPhase = String(payload.phase);
            syncCaseAnalysisProgressTrackForPercentElement(
                element,
                nextText,
                payload.force === true,
                payload.phase || element.dataset.caseAnalysisProgressPhase || null
            );

            const hiddenStatusTarget = element.dataset.caseAnalysisStatusStyle === "hidden";
            if (payload.class_name && !hiddenStatusTarget && element.className !== payload.class_name) {
                element.className = payload.class_name;
            }

            if (Object.prototype.hasOwnProperty.call(payload, "title")) {
                const title = payload.title == null ? "" : String(payload.title);
                if (title) {
                    if (element.title !== title) element.title = title;
                } else if (element.hasAttribute("title")) {
                    element.removeAttribute("title");
                }
            }

            if (payload.status != null && element.dataset.status !== String(payload.status)) {
                element.dataset.status = String(payload.status);
            }
            if (payload.status != null) {
                const stage = element.closest("[data-case-analysis-stage]");
                if (stage && stage.dataset.status !== String(payload.status)) {
                    stage.dataset.status = String(payload.status);
                }
            }
        });
    };

    window.Turbo.StreamActions.case_analysis_progress_snapshot = function () {
        const payload = turboStreamJsonPayload(this);
        document.dispatchEvent(new CustomEvent("case-analysis:progress-snapshot", { detail: payload }));
    };

    window.Turbo.StreamActions.case_analysis_progress = function () {
        const payload = turboStreamJsonPayload(this);

        this.targetElements.forEach((element) => {
            if (!caseAnalysisLiveTargetAllowed(element)) return;

            setCaseAnalysisProgressTrackElement(element, payload.value, payload.force === true || strategyPipelinePhase(payload.phase), payload.phase);
            const fill = element.querySelector(".progress-bar");
            if (payload.bar_class_name && fill.className !== payload.bar_class_name) {
                fill.className = payload.bar_class_name;
            }
        });
    };

    window.Turbo.StreamActions.case_analysis_status = function () {
        const payload = turboStreamJsonPayload(this);

        this.targetElements.forEach((element) => {
            if (!caseAnalysisLiveTargetAllowed(element)) return;

            if (element.hasAttribute("data-case-analysis-status-activity")) {
                if (payload.activity_class_name && element.className !== payload.activity_class_name) {
                    element.className = payload.activity_class_name;
                }
                const label = element.querySelector("[data-case-analysis-status-label]");
                const nextLabel = payload.active_label == null ? "" : String(payload.active_label);
                if (label && label.textContent !== nextLabel) label.textContent = nextLabel;
            }

            if (element.hasAttribute("data-case-analysis-status-pill")) {
                if (payload.terminal_class_name && element.className !== payload.terminal_class_name) {
                    element.className = payload.terminal_class_name;
                }
                const nextLabel = payload.terminal_label == null ? "" : String(payload.terminal_label);
                if (element.textContent !== nextLabel) element.textContent = nextLabel;
            }

            if (payload.status != null && element.dataset.status !== String(payload.status)) {
                element.dataset.status = String(payload.status);
            }
        });
    };

    window.Turbo.StreamActions.case_analysis_stream = function () {
        const payload = turboStreamJsonPayload(this);

        this.targetElements.forEach((element) => {
            element.dispatchEvent(new CustomEvent("case-analysis-stream:event", {
                detail: payload,
                bubbles: true
            }));
        });
    };

    window.Turbo.StreamActions.case_document_refresh = function () {
        document.dispatchEvent(new CustomEvent("case-document:refresh", {
            detail: turboStreamJsonPayload(this)
        }));
    };

    window.Turbo.StreamActions.case_analysis_history_refresh = function () {
        document.dispatchEvent(new CustomEvent("data-tables:refresh", {
            detail: turboStreamJsonPayload(this)
        }));
    };

    window.Turbo.StreamActions.data_tables_refresh = function () {
        document.dispatchEvent(new CustomEvent("data-tables:refresh", {
            detail: turboStreamJsonPayload(this)
        }));
    };
}

function removeTurboProgressBar() {
    document.querySelectorAll(".turbo-progress-bar").forEach((element) => element.remove());
}

removeTurboProgressBar();

// A visit replaced by a newer click aborts its request; that is expected, not an error.
window.addEventListener("unhandledrejection", (event) => {
  if (event.reason?.name === "AbortError") event.preventDefault();
});
document.addEventListener("turbo:before-visit", removeTurboProgressBar);

// The tapped link (or table row) shows it is loading until the page or view
// arrives, and a second tap cannot cancel the visit already under way.
document.addEventListener("turbo:click", (event) => {
  const link = event.target.closest("a, tr[data-href]");
  if (link) link.classList.add("is-loading");
});
const clearLoadingLinks = () => document.querySelectorAll(".is-loading").forEach((element) => element.classList.remove("is-loading"));
["turbo:load", "turbo:frame-load", "turbo:fetch-request-error", "turbo:frame-missing"].forEach((name) => document.addEventListener(name, clearLoadingLinks));
document.addEventListener("turbo:before-visit", cancelCaseAnalysisPanelLoad);
document.addEventListener("turbo:before-fetch-request", removeTurboProgressBar);
document.addEventListener("turbo:before-render", removeTurboProgressBar);
document.addEventListener("turbo:render", removeTurboProgressBar);
document.addEventListener("turbo:load", removeTurboProgressBar);

new MutationObserver(removeTurboProgressBar).observe(document.documentElement, {
    childList: true
});

// Initialize popovers (or tooltips, if used)
let popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
popoverTriggerList.map(function (popoverTriggerEl) {
    return new bootstrap.Popover(popoverTriggerEl);
});

const FLASH_AUTO_DISMISS_DELAY = 5000;

function scheduleAlertAutoDismiss(alertEl) {
    if (!alertEl || alertEl.dataset.flashAutoDismissScheduled === "true") return;

    alertEl.dataset.flashAutoDismissScheduled = "true";
    window.setTimeout(() => {
        if (!document.body.contains(alertEl)) return;

        const instance = bootstrap.Alert.getOrCreateInstance(alertEl);
        instance.close();
    }, FLASH_AUTO_DISMISS_DELAY);
}

function scheduleFlashAutoDismiss(root = document) {
    const alerts = root instanceof Element && root.matches(".alert-dismissible")
        ? [root]
        : Array.from(root.querySelectorAll?.(".alert-dismissible") || []);

    alerts.forEach((alertEl) => {
        bootstrap.Alert.getOrCreateInstance(alertEl);
        scheduleAlertAutoDismiss(alertEl);
    });
}

document.addEventListener("DOMContentLoaded", () => scheduleFlashAutoDismiss());
document.addEventListener("turbo:load", () => scheduleFlashAutoDismiss());
document.addEventListener("turbo:render", () => scheduleFlashAutoDismiss());
document.addEventListener("turbo:frame-load", (event) => scheduleFlashAutoDismiss(event.target));

new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) scheduleFlashAutoDismiss(node);
        });
    });
}).observe(document.documentElement, {
    childList: true,
    subtree: true
});

function select2Available() {
    return Boolean(window.jQuery && window.jQuery.fn && window.jQuery.fn.select2);
}

function initializeBootstrapSelect2(select, options = {}, attempts = 0) {
    if (!select) return false;
    if (!select2Available()) {
        if (attempts < 12) window.setTimeout(() => initializeBootstrapSelect2(select, options, attempts + 1), 250);
        return false;
    }

    const $select = window.jQuery(select);
    if ($select.hasClass('select2-hidden-accessible')) return true;

    const modal = $select.closest('.modal');
    const select2Options = {
        width: '100%',
        theme: 'bootstrap-5',
        selectionCssClass: ':all:',
        dropdownCssClass: ':all:',
        ...options
    };

    if (modal.length) {
        select2Options.dropdownParent = modal;
    }

    $select.select2(select2Options);
    return true;
}

function destroyBootstrapSelect2(root = document) {
    if (!select2Available()) return;

    root.querySelectorAll('select.select2-hidden-accessible').forEach((select) => {
        window.jQuery(select).select2('destroy');
    });
}

function initializeCaseDocumentAssociationSelects(root = document) {
    root.querySelectorAll('.js-case-document-association-select').forEach((select) => {
        initializeBootstrapSelect2(select, {
            allowClear: true,
            placeholder: select.dataset.placeholder || 'Select related event'
        });
    });
}

function initializeCaseAnalysisFocusSelects(root = document) {
    root.querySelectorAll('[data-case-analysis-focus]').forEach((select) => {
        initializeBootstrapSelect2(select, {
            placeholder: select.dataset.placeholder || 'Select query focus',
            minimumResultsForSearch: 0
        });
    });
}

function caseAnalysisDocumentMatcher(params, data) {
    const term = (params.term || '').toString().trim().toLowerCase();
    if (!term) return data;
    if (!data.element) return null;

    const searchText = [
        data.text,
        data.element.dataset.search,
        data.element.dataset.tagIds
    ].join(' ').toLowerCase();

    return searchText.includes(term) ? data : null;
}

const CASE_ANALYSIS_SELECT_ALL_DOCUMENTS_VALUE = '__case_analysis_select_all_documents__';

function initializeCaseAnalysisDocumentSelects(root = document) {
    root.querySelectorAll('[data-case-analysis-tag-filter]').forEach((select) => {
        initializeBootstrapSelect2(select, {
            placeholder: select.dataset.placeholder || 'All tags',
            closeOnSelect: false,
            allowClear: true,
            minimumResultsForSearch: 0
        });
    });

    root.querySelectorAll('[data-case-analysis-document-select]').forEach((select) => {
        const optionsUrl = select.dataset.caseAnalysisDocumentOptionsUrl;
        // With an options URL the documents are searched on the server, 20 at a time.
        const source = optionsUrl ? {
            ajax: {
                url: optionsUrl,
                dataType: 'json',
                delay: 250,
                data: (params) => ({
                    q: params.term || '',
                    page: params.page || 1,
                    tag_ids: Array.from(select.form?.querySelector('[data-case-analysis-tag-filter]')?.selectedOptions || [])
                        .map((option) => option.value).filter(Boolean)
                }),
                processResults: (data) => data
            }
        } : { matcher: caseAnalysisDocumentMatcher };
        initializeBootstrapSelect2(select, {
            placeholder: select.dataset.placeholder || 'All matching documents',
            closeOnSelect: false,
            allowClear: true,
            minimumResultsForSearch: 0,
            ...source
        });
    });
}

function initializeTagSelects(root = document) {
    if (!select2Available()) return;

    root.querySelectorAll('.js-tag-select').forEach((select) => {
        // Batch ingest / plain tags: selection only from existing options (no free-form create).
        // Free-form `tags: true` caused hang/UX issues when Select2 or assets failed to load.
        const allowCreate = select.dataset.allowTagCreate === 'true';
        initializeBootstrapSelect2(select, {
            placeholder: select.dataset.placeholder || 'Add tags',
            closeOnSelect: false,
            tags: allowCreate,
            tokenSeparators: allowCreate ? [','] : [],
            createTag: allowCreate
                ? (params) => {
                    const term = (params.term || '').replace(/\s+/g, ' ').trim();
                    if (!term) return null;
                    return { id: term, text: term, newTag: true };
                }
                : undefined
        });
    });
}

function initializeGenericSelect2(root = document) {
    root.querySelectorAll('select.js-select2').forEach((select) => {
        initializeBootstrapSelect2(select, {
            placeholder: select.dataset.placeholder || 'Select an option',
            allowClear: select.dataset.allowClear !== 'false',
            minimumResultsForSearch: 0
        });
    });
}

function initializeAssociationReviewSelects(root = document, attempts = 0) {
    if (!select2Available()) {
        if (attempts >= 12) return;
        window.setTimeout(() => initializeAssociationReviewSelects(root, attempts + 1), 250);
        return;
    }

    root.querySelectorAll('.association-review-select').forEach((select) => {
        const ajaxUrl = select.dataset.associationOptionsUrl;
        const selectOptions = {
            allowClear: true,
            closeOnSelect: false,
            placeholder: select.dataset.placeholder || 'Search events'
        };

        if (ajaxUrl) {
            selectOptions.ajax = {
                url: ajaxUrl,
                dataType: 'json',
                delay: 150,
                cache: true,
                data: (params) => ({
                    q: params.term || '',
                    page: params.page || 1,
                    exclude_id: select.dataset.associationExcludeId || ''
                }),
                processResults: (data) => ({
                    results: data.results || [],
                    pagination: { more: Boolean(data.pagination && data.pagination.more) }
                })
            };
            selectOptions.minimumInputLength = 0;
        }

        initializeBootstrapSelect2(select, selectOptions);
    });

    root.querySelectorAll('.association-review-taxonomy-select').forEach((select) => {
        initializeBootstrapSelect2(select, {
            allowClear: true,
            closeOnSelect: true,
            placeholder: select.dataset.placeholder || 'Select value'
        });
    });
}

// An association row's select holds only its own value; the first time it
// opens it takes the full list from the page's #association_select_options.
function fillAssociationOptions(select) {
    if (!select || select.dataset.associationOptionsFilled === 'true') return;
    const template = document.getElementById('association_select_options');
    const source = template?.content.querySelector(`select[data-options-key="${select.dataset.associationOptionsKey}"]`);
    if (!source) return;

    const selected = new Set(Array.from(select.selectedOptions).map((option) => option.value));
    const options = Array.from(source.options).map((option) => {
        const copy = option.cloneNode(true);
        copy.selected = selected.has(copy.value);
        return copy;
    });
    // Keep a value the list does not have (an older or free-form one).
    Array.from(select.options).forEach((option) => {
        if (option.value && !options.some((copy) => copy.value === option.value)) options.push(option.cloneNode(true));
    });
    select.replaceChildren(...options);
    select.dataset.associationOptionsFilled = 'true';
    const row = select.closest('[data-taxonomy-row]');
    if (row) syncTaxonomyRow(row);
}

if (!window.associationOptionsBound) {
    window.associationOptionsBound = true;
    const fillFromEvent = (event) => {
        const select = event.target.closest?.('select[data-association-options-key]');
        if (select) fillAssociationOptions(select);
    };
    document.addEventListener('mousedown', fillFromEvent, true);
    document.addEventListener('focusin', fillFromEvent, true);
    document.addEventListener('keydown', fillFromEvent, true);
    if (window.jQuery) {
        window.jQuery(document).on('select2:opening', 'select[data-association-options-key]', (event) => fillAssociationOptions(event.currentTarget));
    }
}

function closeAssociationReviewSelects() {
    if (!select2Available()) return;

    window.jQuery('.association-review-select.select2-hidden-accessible, .association-review-tag-select.select2-hidden-accessible, .association-review-taxonomy-select.select2-hidden-accessible').select2('close');
}

const associationAutosaveTimers = new WeakMap();
const associationAutosaveControllers = new WeakMap();

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function associationAutosaveFormForControl(control) {
    if (!control) return null;

    const directForm = control.form;
    if (directForm?.matches?.('[data-association-autosave-form]')) return directForm;

    const formId = control.getAttribute?.('form');
    const form = formId ? document.getElementById(formId) : null;
    return form?.matches?.('[data-association-autosave-form]') ? form : null;
}

function associationAutosaveStatus(form) {
    return form.closest('[data-association-review-row]')?.querySelector('[data-association-autosave-status]');
}

function setAssociationAutosaveStatus(form, state, message) {
    const status = associationAutosaveStatus(form);
    if (!status) return;

    status.dataset.state = state;
    status.textContent = message;
    status.classList.remove('text-body-secondary', 'text-success', 'text-danger', 'text-info');

    if (state === 'error') {
        status.classList.add('text-danger');
    } else if (state === 'saving') {
        status.classList.add('text-info');
    } else {
        status.classList.add('text-body-secondary');
    }
}

function scheduleAssociationAutosave(form, delay = 300) {
    window.clearTimeout(associationAutosaveTimers.get(form));
    setAssociationAutosaveStatus(form, 'pending', 'Pending');

    associationAutosaveTimers.set(form, window.setTimeout(() => {
        submitAssociationAutosave(form);
    }, delay));
}

async function submitAssociationAutosave(form) {
    const existingController = associationAutosaveControllers.get(form);
    if (existingController) existingController.abort();

    const controller = new AbortController();
    associationAutosaveControllers.set(form, controller);
    setAssociationAutosaveStatus(form, 'saving', 'Saving...');

    try {
        const response = await fetch(form.action, {
            method: (form.method || 'post').toUpperCase(),
            body: new FormData(form),
            credentials: 'same-origin',
            signal: controller.signal,
            headers: {
                Accept: 'application/json',
                'X-CSRF-Token': csrfToken(),
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || `Save failed (${response.status})`);

        setAssociationAutosaveStatus(form, 'saved', 'Saved');
        document.dispatchEvent(new CustomEvent('case-document:refresh', { detail: payload }));
    } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('Association autosave failed', error);
        setAssociationAutosaveStatus(form, 'error', 'Error');
    } finally {
        if (associationAutosaveControllers.get(form) === controller) {
            associationAutosaveControllers.delete(form);
        }
    }
}

function normalizedTaxonomyValue(value) {
    return (value || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .split('_')
        .map((token) => {
            if (token.endsWith('ies')) return `${token.slice(0, -3)}y`;
            if (token.endsWith('s') && !/(ss|us|is)$/.test(token)) return token.slice(0, -1);
            return token;
        })
        .join('_');
}

function syncTaxonomyRow(row) {
    const category = row.querySelector('[data-taxonomy-category-select]');
    const subcategory = row.querySelector('[data-taxonomy-subcategory-select]');
    if (!(category && subcategory)) return;

    const categoryValue = normalizedTaxonomyValue(category.value);
    const subcategoryValue = normalizedTaxonomyValue(subcategory.value);
    let changed = false;

    Array.from(subcategory.options).forEach((option) => {
        const optionValue = normalizedTaxonomyValue(option.value);
        const allowedCategories = (option.dataset.taxonomyCategories || '').split(/\s+/).filter(Boolean);
        const parentMismatch = Boolean(categoryValue && allowedCategories.length > 0 && !allowedCategories.includes(categoryValue));
        option.disabled = parentMismatch || Boolean(categoryValue && optionValue && optionValue === categoryValue);
    });

    if (categoryValue && subcategoryValue && categoryValue === subcategoryValue) {
        subcategory.value = '';
        changed = true;
    }

    if (changed && select2Available()) {
        window.jQuery(subcategory).val('').trigger('change');
    }
}

function bindTaxonomyRows(root = document) {
    root.querySelectorAll('[data-taxonomy-row]').forEach((row) => {
        syncTaxonomyRow(row);
        row.querySelectorAll('[data-taxonomy-category-select], [data-taxonomy-subcategory-select]').forEach((select) => {
            if (select.dataset.taxonomyBound === 'true') return;

            select.dataset.taxonomyBound = 'true';
            select.addEventListener('change', () => syncTaxonomyRow(row));
        });
    });
}

function visibleAssociationReviewRows(root = document) {
    return Array.from(root.querySelectorAll('[data-association-review-row]')).filter((row) => !row.hidden);
}

function visibleAssociationDiscoveryCheckboxes(root = document) {
    return visibleAssociationReviewRows(root)
        .map((row) => row.querySelector('[data-association-discovery-checkbox]'))
        .filter(Boolean);
}

function updateAssociationDiscoveryMaster(root = document) {
    const master = root.querySelector('[data-association-discovery-master]');
    if (!master) return;

    const checkboxes = visibleAssociationDiscoveryCheckboxes(root);
    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;

    master.disabled = checkboxes.length === 0;
    master.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
    master.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
}

function bindAssociationDiscovery(root = document) {
    const master = root.querySelector('[data-association-discovery-master]');
    if (master && master.dataset.bound !== 'true') {
        master.dataset.bound = 'true';
        master.addEventListener('change', () => {
            visibleAssociationDiscoveryCheckboxes(root).forEach((checkbox) => {
                checkbox.checked = master.checked;
            });
            updateAssociationDiscoveryMaster(root);
        });
    }

    root.querySelectorAll('[data-association-discovery-checkbox]').forEach((checkbox) => {
        if (checkbox.dataset.bound === 'true') return;

        checkbox.dataset.bound = 'true';
        checkbox.addEventListener('change', () => updateAssociationDiscoveryMaster(root));
    });

    updateAssociationDiscoveryMaster(root);
}

function bindAssociationReviewPage(root = document) {
    const page = root.querySelector?.('.association-review-page') || document.querySelector('.association-review-page');
    if (!page) return;

    initializeAssociationReviewSelects(page);
    bindTaxonomyRows(page);
    bindAssociationDiscovery(page);

    const filter = page.querySelector('[data-association-review-filter]');
    if (filter && filter.dataset.bound !== 'true') {
        filter.dataset.bound = 'true';
        filter.addEventListener('input', () => {
            const query = filter.value.toLowerCase().trim();
            closeAssociationReviewSelects();
            page.querySelectorAll('[data-association-review-row]').forEach((row) => {
                row.hidden = Boolean(query && !row.dataset.eventSearch.includes(query));
            });
            updateAssociationDiscoveryMaster(page);
        });
    }

    const perPage = page.querySelector('[data-association-review-per-page]');
    if (perPage && perPage.dataset.bound !== 'true') {
        perPage.dataset.bound = 'true';
        perPage.addEventListener('change', () => perPage.form.requestSubmit());
    }

    if (page.dataset.associationAutosaveBound !== 'true') {
        page.dataset.associationAutosaveBound = 'true';

        page.addEventListener('change', (event) => {
            const form = associationAutosaveFormForControl(event.target);
            if (form) scheduleAssociationAutosave(form);
        });

        page.addEventListener('input', (event) => {
            const target = event.target;
            if (!target.matches('input:not([type="checkbox"]):not([type="hidden"]), textarea')) return;

            const form = associationAutosaveFormForControl(target);
            if (form) scheduleAssociationAutosave(form, 500);
        });

        page.addEventListener('submit', (event) => {
            const form = event.target.closest('[data-association-autosave-form]');
            if (!form) return;

            event.preventDefault();
            scheduleAssociationAutosave(form, 0);
        });
    }

    if (select2Available() && page.dataset.associationAutosaveSelect2Bound !== 'true') {
        page.dataset.associationAutosaveSelect2Bound = 'true';
        window.jQuery(page).on('change.associationAutosave', 'select[form]', function () {
            const form = associationAutosaveFormForControl(this);
            if (form) scheduleAssociationAutosave(form);
        });
    }

    page.querySelectorAll('[data-association-clear]').forEach((button) => {
        if (button.dataset.bound === 'true') return;

        button.dataset.bound = 'true';
        button.addEventListener('click', () => {
            const control = button.closest('[data-association-link-control]');
            const select = control?.querySelector('select');
            if (!select) return;

            if (select2Available()) {
                window.jQuery(select).val(null).trigger('change');
            } else {
                Array.from(select.options).forEach((option) => {
                    option.selected = false;
                });
            }
            select.dispatchEvent(new Event('change', { bubbles: true }));
        });
    });
}

function initializeCaseDocumentAssociationForms(root = document) {
    root.querySelectorAll('.case-document-associations').forEach((container) => {
        if (container.dataset.bound === 'true') {
            initializeCaseDocumentAssociationSelects(container);
            return;
        }

        container.dataset.bound = 'true';

        container.addEventListener('click', (event) => {
            const addButton = event.target.closest('[data-association-add]');
            if (addButton) {
                event.preventDefault();
                const template = container.querySelector('template[data-association-template]');
                const rows = container.querySelector('[data-association-rows]');
                if (!(template && rows)) return;

                const fragment = template.content.cloneNode(true);
                rows.appendChild(fragment);
                initializeCaseDocumentAssociationSelects(container);
                return;
            }

            const removeButton = event.target.closest('[data-association-remove]');
            if (!removeButton) return;

            event.preventDefault();
            const row = removeButton.closest('[data-association-row]');
            const rows = container.querySelectorAll('[data-association-row]');
            if (!row) return;

            if (rows.length === 1) {
                row.querySelectorAll('select, input').forEach((field) => {
                    if (field.tagName === 'SELECT') {
                        field.value = '';
                        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
                            window.jQuery(field).val(null).trigger('change');
                        }
                    } else {
                        field.value = '';
                    }
                });
                return;
            }

            row.remove();
        });

        initializeCaseDocumentAssociationSelects(container);
    });
}

function initializeCaseDocumentModals(root = document) {
    if (!window.bootstrap) return;

    root.querySelectorAll('[data-open-modal="true"]').forEach((modalElement) => {
        window.bootstrap.Modal.getOrCreateInstance(modalElement).show();
        modalElement.dataset.openModal = 'false';
    });
}

function initializeCaseDocumentList(root = document) {
    const searchInput = root.querySelector('[data-file-search-input]');
    const rows = Array.from(root.querySelectorAll('[data-file-row]'));
    const checkboxes = Array.from(root.querySelectorAll('[data-file-checkbox]'));
    const categorySections = Array.from(root.querySelectorAll('[data-file-category-section]'));
    const bulkButton = root.querySelector('[data-bulk-download-button]');
    const selectionSummary = root.querySelector('[data-file-selection-summary]');

    if (!rows.length) return;

    const formatBytes = (bytes) => {
        if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
        if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${bytes} B`;
    };

    const pluralizeFiles = (count) => `${count} ${count === 1 ? 'file' : 'files'}`;

    const updateSelectionState = () => {
        const selected = checkboxes.filter((checkbox) => checkbox.checked);

        if (bulkButton) bulkButton.disabled = selected.length === 0;

        if (!selectionSummary) return;

        if (selected.length === 0) {
            selectionSummary.textContent = pluralizeFiles(rows.length);
            return;
        }

        const totalBytes = selected.reduce((sum, checkbox) => {
            const row = checkbox.closest('[data-file-row]');
            return sum + Number(row?.dataset.fileSize || 0);
        }, 0);

        selectionSummary.textContent = `${pluralizeFiles(selected.length)} selected size: ${formatBytes(totalBytes)}`;
    };

    if (searchInput && searchInput.dataset.bound !== 'true') {
        searchInput.dataset.bound = 'true';

        searchInput.addEventListener('input', () => {
            const query = searchInput.value.trim().toLowerCase();

            rows.forEach((row) => {
                const matches = !query || row.dataset.filename.includes(query);
                row.hidden = !matches;
            });

            categorySections.forEach((section) => {
                const visibleRows = Array.from(section.querySelectorAll('[data-file-row]')).some((row) => !row.hidden);
                section.hidden = !visibleRows;
            });
        });
    }

    checkboxes.forEach((checkbox) => {
        if (checkbox.dataset.bound === 'true') return;

        checkbox.dataset.bound = 'true';
        checkbox.addEventListener('change', updateSelectionState);
    });

    updateSelectionState();
}

function chatOffcanvasElement(root = document) {
    return root.querySelector("#offcanvasScrolling");
}

function chatMessagesContainers(root = document) {
    const containers = [];
    if (root && root.matches && root.matches(".chat-messages-list")) containers.push(root);
    if (root && root.querySelectorAll) containers.push(...root.querySelectorAll(".chat-messages-list"));
    return Array.from(new Set(containers));
}

function syncChatMessageSides(container) {
    if (!container) return;

    const currentUserId = container.dataset.chatCurrentUserId;
    if (!currentUserId) return;

    container.querySelectorAll(".chat-message-row[data-chat-message-user-id]").forEach((row) => {
        const sentByCurrentUser = row.dataset.chatMessageUserId === currentUserId;
        const stack = row.querySelector(".chat-message-stack");
        const content = row.querySelector(".chat-message-content");
        const bubble = row.querySelector(".chat-message-bubble");

        row.classList.toggle("is-chat-current-user", sentByCurrentUser);
        row.classList.toggle("justify-content-end", sentByCurrentUser);
        row.classList.toggle("justify-content-start", !sentByCurrentUser);
        stack?.classList.toggle("flex-row-reverse", sentByCurrentUser);
        content?.classList.toggle("text-end", sentByCurrentUser);
        bubble?.classList.toggle("text-bg-primary", sentByCurrentUser);
        bubble?.classList.toggle("bg-body-tertiary", !sentByCurrentUser);
        bubble?.classList.toggle("border", !sentByCurrentUser);
        bubble?.classList.toggle("text-body", !sentByCurrentUser);
    });
}

function scrollChatToBottom(root = document) {
    chatMessagesContainers(root).forEach((messages) => {
        syncChatMessageSides(messages);
        messages.scrollTop = messages.scrollHeight;
    });
}

function updateChatViewportFrame(root = document) {
    const offcanvas = chatOffcanvasElement(root) || chatOffcanvasElement(document);
    if (!offcanvas) return;

    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportTop = viewport?.offsetTop || 0;
    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

    offcanvas.style.setProperty("--chat-visual-height", `${Math.round(viewportHeight)}px`);
    offcanvas.style.setProperty("--chat-visual-top", `${Math.max(0, Math.round(viewportTop))}px`);
}

function percentValue(value) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, parsed));
}

function applyDataDrivenStyles(root = document) {
    root.querySelectorAll('[data-progress-width]').forEach((element) => {
        if (element.classList.contains("case-analysis-progress-track")) return;

        const fill = element.firstElementChild;
        if (!fill) return;

        fill.style.width = `${percentValue(element.dataset.progressWidth)}%`;
    });

    root.querySelectorAll('[data-timeline-color]').forEach((element) => {
        const color = element.dataset.timelineColor;
        if (color) element.style.backgroundColor = color;
    });

    syncCaseAnalysisProgressTracks(root);
}

function shouldDeferChatAutofocus() {
    return window.matchMedia?.("(max-width: 575.98px), (hover: none) and (pointer: coarse)").matches;
}

function focusChatInput(root = document, options = {}) {
    const input = root.querySelector("#chat-text");
    if (!input) return;
    if (!options.force && shouldDeferChatAutofocus()) return;

    window.requestAnimationFrame(() => input.focus());
}

function initializeChat(root = document) {
    const offcanvas = chatOffcanvasElement(root);
    if (offcanvas && offcanvas.dataset.chatBound !== "true") {
        offcanvas.dataset.chatBound = "true";
        offcanvas.addEventListener("shown.bs.offcanvas", () => {
            updateChatViewportFrame(document);
            scrollChatToBottom(document);
            focusChatInput(document);
        });
        offcanvas.addEventListener("hidden.bs.offcanvas", () => {
            offcanvas.style.removeProperty("--chat-visual-height");
            offcanvas.style.removeProperty("--chat-visual-top");
        });
    }

    if (offcanvas && offcanvas.dataset.chatViewportBound !== "true") {
        offcanvas.dataset.chatViewportBound = "true";
        const syncChatViewport = () => {
            window.requestAnimationFrame(() => updateChatViewportFrame(document));
        };

        window.addEventListener("resize", syncChatViewport);
        window.visualViewport?.addEventListener("resize", syncChatViewport);
        window.visualViewport?.addEventListener("scroll", syncChatViewport);
        offcanvas.addEventListener("focusin", syncChatViewport);
        offcanvas.addEventListener("focusout", syncChatViewport);
    }

    chatMessagesContainers(root).forEach((messages) => {
        syncChatMessageSides(messages);

        if (messages.dataset.chatObserved === "true" || typeof MutationObserver === "undefined") return;

        messages.dataset.chatObserved = "true";
        new MutationObserver(() => scrollChatToBottom(messages)).observe(messages, {
            childList: true,
            subtree: true
        });
    });

    scrollChatToBottom(root);
    window.requestAnimationFrame(() => {
        scrollChatToBottom(root);
        window.requestAnimationFrame(() => scrollChatToBottom(root));
    });
}

function ensureRemoteContentModal() {
    let modal = document.getElementById('remoteContentModal');
    if (!modal) {
        modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'remoteContentModal';
        modal.tabIndex = -1;
        modal.setAttribute('aria-labelledby', 'remoteContentModalLabel');
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable modal-lg" data-remote-modal-dialog>
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="modal-title h5" id="remoteContentModalLabel" data-remote-modal-title>Details</h2>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" id="remoteContentModalBody" data-remote-modal-body></div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
    }

    return {
        modal,
        dialog: modal.querySelector('[data-remote-modal-dialog]'),
        title: modal.querySelector('[data-remote-modal-title]'),
        body: modal.querySelector('[data-remote-modal-body]')
    };
}

function applyRemoteModalSize(dialog, size) {
    if (!dialog) return;

    dialog.classList.remove('modal-sm', 'modal-lg', 'modal-xl');
    const normalizedSize = String(size || 'lg').trim();
    if (['sm', 'lg', 'xl'].includes(normalizedSize)) {
        dialog.classList.add(`modal-${normalizedSize}`);
    } else {
        dialog.classList.add('modal-lg');
    }
}

async function openRemoteContentModal(trigger) {
    if (!window.bootstrap) return;

    const url = trigger.dataset.remoteModalUrl;
    if (!url) return;

    const { modal, dialog, title, body } = ensureRemoteContentModal();
    applyRemoteModalSize(dialog, trigger.dataset.remoteModalSize);
    if (title) title.textContent = trigger.dataset.remoteModalTitle || 'Details';
    if (body) body.innerHTML = '<div class="text-muted py-4 text-center">Loading...</div>';

    const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modal);
    modalInstance.show();

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'text/html',
                'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin'
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        body.innerHTML = await response.text();
        destroyBootstrapSelect2(body);
        initializeGenericSelect2(body);
        initializeCaseDocumentAssociationForms(body);
        initializeCaseAnalysisForms(body);
    } catch (error) {
        body.innerHTML = `
            <div class="alert alert-danger mb-0">
                <strong>Could not load this record.</strong>
                <div class="small mt-1">${escapeHTML(error.message || error)}</div>
            </div>
        `;
    }
}

function ensureCaseDocumentDocumentModal() {
    let modal = document.getElementById('caseDocumentDocumentModal');
    if (!modal) {
        modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'caseDocumentDocumentModal';
        modal.tabIndex = -1;
        modal.setAttribute('aria-labelledby', 'caseDocumentDocumentModalLabel');
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="modal-title h5 text-truncate" id="caseDocumentDocumentModalLabel" data-case-document-document-title>Event Document</h2>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body p-0">
                        <iframe class="w-100 vh-100 border-0" data-case-document-document-frame title="Event Document"></iframe>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        modal.addEventListener('hidden.bs.modal', () => {
            const frame = modal.querySelector('[data-case-document-document-frame]');
            if (frame) frame.removeAttribute('src');
        });
    }

    return {
        modal,
        title: modal.querySelector('[data-case-document-document-title]'),
        frame: modal.querySelector('[data-case-document-document-frame]')
    };
}

function openCaseDocumentDocumentModal(trigger) {
    if (!window.bootstrap) return;

    const url = trigger.dataset.caseDocumentDocumentUrl;
    if (!url) return;

    const { modal, title, frame } = ensureCaseDocumentDocumentModal();
    if (title) title.textContent = trigger.dataset.caseDocumentDocumentTitle || 'Event Document';
    if (frame) frame.src = url;
    window.bootstrap.Modal.getOrCreateInstance(modal).show();
}

function initializeCaseAnalysisForms(root = document) {
    root.querySelectorAll('[data-case-analysis-form]').forEach((form) => {
        if (form.dataset.caseAnalysisInitialized === 'true') {
            // Turbo cache restoration may remove Select2's generated markup while retaining
            // the form node. Restore only those widgets; never rebuild this entire initializer.
            initializeCaseAnalysisFocusSelects(form);
            initializeCaseAnalysisDocumentSelects(form);
            return;
        }

        const endpointSelect = form.querySelector('[data-case-analysis-endpoint]');
        const modelLabel = form.querySelector('[data-case-analysis-model-label]');
        const modelInput = form.querySelector('[data-case-analysis-model-input]');
        const focusSelect = form.querySelector('[data-case-analysis-focus]');
        const queryInput = form.querySelector('[data-case-analysis-query]');
        const suggestionList = form.querySelector('[data-case-analysis-suggestion-list]');
        const refreshSuggestionsButton = form.querySelector('[data-case-analysis-refresh-suggestions]');
        const tagFilterSelect = form.querySelector('[data-case-analysis-tag-filter]');
        const documentSelect = form.querySelector('[data-case-analysis-document-select]');
        const documentCount = form.querySelector('[data-case-analysis-document-count]');
        const documentScopeSummary = form.querySelector('[data-case-analysis-document-scope-summary]');
        const submitButton = form.querySelector('[data-case-analysis-submit-button]');

        initializeCaseAnalysisFocusSelects(form);
        initializeCaseAnalysisDocumentSelects(form);

        const parseModelChoices = () => {
            try {
                const rawChoices = JSON.parse(form.dataset.caseAnalysisModelChoices || '{}');
                if (!rawChoices || typeof rawChoices !== "object" || Array.isArray(rawChoices)) return {};
                const normalizedChoicesByEndpoint = {};

                Object.entries(rawChoices).forEach(([endpoint, choices]) => {
                    normalizedChoicesByEndpoint[normalizeEndpoint(endpoint)] = normalizeModelChoices(choices);
                });

                return normalizedChoicesByEndpoint;
            } catch (_error) {
                return {};
            }
        };

        const normalizeEndpoint = (endpoint) => {
            const value = String(endpoint || "").trim();
            if (!value) return "";

            const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
            try {
                const parsed = new URL(withScheme);
                const path = parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/+$/g, "");
                const port = parsed.port ? `:${parsed.port}` : "";
                return `${parsed.protocol}//${parsed.host}${port}${path}`.toLowerCase();
            } catch (_error) {
                return value.replace(/\/+$/g, "").toLowerCase();
            }
        };

        const normalizeModelChoices = (choices) => {
            return (Array.isArray(choices) ? choices : [])
                .map((choice) => {
                    const label = Array.isArray(choice) ? choice[0] : choice;
                    const value = Array.isArray(choice) ? choice[1] : choice;
                    if (!(label && value)) return null;
                    return [label, value];
                })
                .filter(Boolean);
        };

        const storeModelChoices = (endpoint, choices) => {
            const normalizedEndpoint = normalizeEndpoint(endpoint);
            if (!normalizedEndpoint) return;

            const normalizedChoices = normalizeModelChoices(choices);
            if (!normalizedChoices.length) return;

            const allChoices = parseModelChoices();
            allChoices[normalizedEndpoint] = normalizedChoices;
            form.dataset.caseAnalysisModelChoices = JSON.stringify(allChoices);
        };

        const modelChoicesForEndpoint = (endpoint = null) => {
            const selectedEndpoint = normalizeEndpoint(endpoint || endpointSelect?.value || form.dataset.caseAnalysisCurrentEndpoint || '');
            const choices = parseModelChoices();
            return choices[selectedEndpoint] || choices[Object.keys(choices)[0]] || [];
        };

        const rebuildModelOptions = (choices = null, { preserveSelected = true } = {}) => {
            if (!modelInput) return;

            const modelChoices = normalizeModelChoices(choices || modelChoicesForEndpoint());
            if (!modelChoices.length) return;

            const previousValue = modelInput.value;
            const selectedValue = preserveSelected && modelChoices.some((choice) => choice[1] === previousValue) ? previousValue : modelChoices[0][1];
            modelInput.replaceChildren();
            modelChoices.forEach(([label, value]) => {
                const option = document.createElement('option');
                option.value = value;
                option.textContent = label;
                option.selected = value === selectedValue;
                modelInput.appendChild(option);
            });

            modelInput.value = selectedValue;
            modelInput.dispatchEvent(new Event('change', { bubbles: true }));
        };

        const fetchModelOptions = async (endpoint) => {
            const optionsUrl = form.dataset.caseAnalysisModelOptionsUrl;
            const normalizedEndpoint = normalizeEndpoint(endpoint);
            if (!optionsUrl || !normalizedEndpoint) return;
            if (form.dataset.caseAnalysisModelFetchEndpoint === normalizedEndpoint) return;

            form.dataset.caseAnalysisModelFetchEndpoint = normalizedEndpoint;

            try {
                const url = new URL(optionsUrl, window.location.origin);
                url.searchParams.set('text_base_uri', normalizedEndpoint);
                const response = await fetch(url.toString(), {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin'
                });
                if (!response.ok) return;

                const payload = await response.json();
                const resolvedEndpoint = normalizeEndpoint(payload.endpoint || normalizedEndpoint);
                const choices = normalizeModelChoices(payload.models || []);
                storeModelChoices(resolvedEndpoint, choices);
                if (endpointSelect && normalizeEndpoint(endpointSelect.value) === resolvedEndpoint) {
                    rebuildModelOptions(choices);
                }
            } catch (_error) {
                // Embedded model choices remain as the fallback if live discovery is unavailable.
            } finally {
                if (form.dataset.caseAnalysisModelFetchEndpoint === normalizedEndpoint) {
                    delete form.dataset.caseAnalysisModelFetchEndpoint;
                }
            }
        };

        const updateModelLabel = () => {
            if (!modelLabel) return;

            const selectedLabel = endpointSelect?.selectedOptions?.[0]?.textContent || '';
            if (/omega/i.test(selectedLabel)) {
                modelLabel.textContent = 'Omega model';
            } else if (/spark/i.test(selectedLabel)) {
                modelLabel.textContent = 'Spark model';
            } else {
                modelLabel.textContent = 'Model';
            }
        };

        const updateModelFields = ({ live = false, preserveSelected = true } = {}) => {
            updateModelLabel();
            const endpoint = normalizeEndpoint(endpointSelect?.value || form.dataset.caseAnalysisCurrentEndpoint || '');
            if (endpointSelect) form.dataset.caseAnalysisCurrentEndpoint = endpoint;
            rebuildModelOptions(modelChoicesForEndpoint(endpoint), { preserveSelected });
            if (live) fetchModelOptions(endpoint);
        };

        const parseSuggestions = () => {
            try {
                return JSON.parse(form.dataset.caseAnalysisSuggestions || '{}');
            } catch (_error) {
                return {};
            }
        };

        const rotateSuggestions = (suggestions) => {
            return suggestions
                .map((value) => ({ value, sort: Math.random() }))
                .sort((a, b) => a.sort - b.sort)
                .slice(0, 3)
                .map((entry) => entry.value);
        };

        const renderSuggestions = ({ forceNew = false } = {}) => {
            if (!(focusSelect && queryInput && suggestionList)) return;

            const allSuggestions = parseSuggestions();
            const focus = focusSelect.value;
            const suggestions = forceNew ? rotateSuggestions(allSuggestions[focus] || []) : (allSuggestions[focus] || []);
            suggestionList.replaceChildren();

            suggestions.slice(0, 3).forEach((suggestion, index) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'btn btn-outline-secondary text-start';
                button.dataset.caseAnalysisSuggestionValue = suggestion;
                button.textContent = `Prompt ${index + 1}: ${suggestion}`;
                button.addEventListener('click', () => {
                    queryInput.value = suggestion;
                    queryInput.dispatchEvent(new Event('input', { bubbles: true }));
                    form.dataset.caseAnalysisLastSuggestion = suggestion;
                    queryInput.focus();
                });
                suggestionList.appendChild(button);
            });

            const firstSuggestion = suggestions[0] || '';
            const currentQuery = queryInput.value.trim();
            const lastSuggestion = form.dataset.caseAnalysisLastSuggestion || '';
            if (firstSuggestion && (!currentQuery || currentQuery === lastSuggestion)) {
                queryInput.value = firstSuggestion;
                form.dataset.caseAnalysisLastSuggestion = firstSuggestion;
            }
        };

        const parseDocumentPayload = () => {
            try {
                const documents = JSON.parse(form.dataset.caseAnalysisDocuments || '[]');
                return Array.isArray(documents) ? documents : [];
            } catch (_error) {
                return [];
            }
        };

        const selectedTagIds = () => {
            if (!tagFilterSelect) return new Set();

            return new Set(
                Array.from(tagFilterSelect.selectedOptions)
                    .map((option) => String(option.value || '').trim())
                    .filter(Boolean)
            );
        };

        const documentMatchesSelectedTags = (document, tagIds) => {
            if (!tagIds.size) return true;

            return (document.tag_ids || [])
                .map((id) => String(id))
                .some((id) => tagIds.has(id));
        };

        const currentDocumentSelection = () => {
            if (!documentSelect) return new Set();

            return new Set(
                Array.from(documentSelect.selectedOptions)
                    .map((option) => String(option.value || '').trim())
                    .filter((value) => value !== CASE_ANALYSIS_SELECT_ALL_DOCUMENTS_VALUE)
                    .filter(Boolean)
            );
        };

        const realDocumentOptions = () => Array.from(documentSelect.options)
            .filter((option) => option.value && option.value !== CASE_ANALYSIS_SELECT_ALL_DOCUMENTS_VALUE);

        const emitDocumentSelectChange = () => {
            if (select2Available()) {
                window.jQuery(documentSelect).trigger('change');
            } else {
                documentSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        };

        const serverDocumentSearch = () => Boolean(documentSelect?.dataset.caseAnalysisDocumentOptionsUrl);

        const updateDocumentSelectionCount = () => {
            if (!documentSelect) return;
            if (serverDocumentSearch()) {
                const selectedCount = realDocumentOptions().filter((option) => option.selected).length;
                const tagCount = selectedTagIds().size;
                if (documentCount) documentCount.textContent = selectedCount > 0 ? `${selectedCount} selected` : 'all in scope';
                if (documentScopeSummary) {
                    documentScopeSummary.textContent = selectedCount > 0
                        ? `The prompt will be limited to ${selectedCount} selected case document${selectedCount === 1 ? "" : "s"}.`
                        : (tagCount > 0
                            ? `The prompt will use every case document matching the selected tag${tagCount === 1 ? "" : "s"}.`
                            : 'The prompt will use every searchable case document for this case.');
                }
                return;
            }

            const availableCount = realDocumentOptions().length;
            const selectedCount = realDocumentOptions().filter((option) => option.selected).length;
            const tagCount = selectedTagIds().size;

            if (documentCount) {
                documentCount.textContent = selectedCount > 0
                    ? `${selectedCount} selected · ${availableCount} matching`
                    : `${availableCount} matching · all in scope`;
            }

            if (documentScopeSummary) {
                if (selectedCount > 0) {
                    documentScopeSummary.textContent = `The prompt will be limited to ${selectedCount} selected case document${selectedCount === 1 ? "" : "s"}.`;
                } else if (tagCount > 0) {
                    documentScopeSummary.textContent = `The prompt will use all ${availableCount} case document${availableCount === 1 ? "" : "s"} matching the selected tag${tagCount === 1 ? "" : "s"}.`;
                } else {
                    documentScopeSummary.textContent = `The prompt will use all ${availableCount} searchable case document${availableCount === 1 ? "" : "s"} for this case.`;
                }
            }
        };

        const appendSelectAllDocumentOption = (availableCount) => {
            if (!availableCount) return;

            const selectAllOption = document.createElement('option');
            selectAllOption.value = CASE_ANALYSIS_SELECT_ALL_DOCUMENTS_VALUE;
            selectAllOption.textContent = 'Select all matching documents';
            selectAllOption.dataset.search = 'select all matching documents';
            documentSelect.appendChild(selectAllOption);
        };

        const applySelectAllDocumentSelection = () => {
            if (!documentSelect) return false;

            const selectAllOption = Array.from(documentSelect.selectedOptions)
                .find((option) => option.value === CASE_ANALYSIS_SELECT_ALL_DOCUMENTS_VALUE);
            if (!selectAllOption) return false;

            realDocumentOptions().forEach((option) => {
                option.selected = true;
            });
            selectAllOption.selected = false;
            emitDocumentSelectChange();
            return true;
        };

        const handleDocumentSelectionChange = () => {
            if (applySelectAllDocumentSelection()) return;

            updateDocumentSelectionCount();
        };

        const rebuildDocumentOptions = () => {
            if (!documentSelect) return;
            // Server search: the picker's next search applies the tags; the count updates now.
            if (serverDocumentSearch()) {
                updateDocumentSelectionCount();
                return;
            }

            const documents = parseDocumentPayload();
            const tagIds = selectedTagIds();
            const previousSelection = currentDocumentSelection();
            const filteredDocuments = documents.filter((document) => documentMatchesSelectedTags(document, tagIds));
            documentSelect.replaceChildren();
            appendSelectAllDocumentOption(filteredDocuments.length);

            filteredDocuments.forEach((caseDocument) => {
                const id = String(caseDocument.id || '').trim();
                if (!id) return;

                const option = document.createElement('option');
                option.value = id;
                option.textContent = caseDocument.label || `CaseDocument #${id}`;
                option.dataset.tagIds = (caseDocument.tag_ids || []).join(' ');
                option.dataset.search = caseDocument.search || option.textContent;
                option.selected = previousSelection.has(id);
                documentSelect.appendChild(option);
            });

            emitDocumentSelectChange();
            updateDocumentSelectionCount();
        };

        if (form.dataset.caseAnalysisBound !== 'true') {
            form.dataset.caseAnalysisBound = 'true';
            if (endpointSelect) {
                endpointSelect.addEventListener('change', () => updateModelFields({ live: true, preserveSelected: false }));
            }
            if (focusSelect) {
                const renderFocusSuggestions = () => renderSuggestions({ forceNew: true });
                focusSelect.addEventListener('change', renderFocusSuggestions);
                if (window.jQuery) window.jQuery(focusSelect).on('change.caseAnalysisFocus', renderFocusSuggestions);
            }
            if (refreshSuggestionsButton) {
                refreshSuggestionsButton.addEventListener('click', () => renderSuggestions({ forceNew: true }));
            }
            if (tagFilterSelect) {
                tagFilterSelect.addEventListener('change', rebuildDocumentOptions);
                if (window.jQuery) window.jQuery(tagFilterSelect).on('change.caseAnalysisTags', rebuildDocumentOptions);
            }
            if (documentSelect) {
                documentSelect.addEventListener('change', handleDocumentSelectionChange);
                if (window.jQuery) window.jQuery(documentSelect).on('change.caseAnalysisDocuments', handleDocumentSelectionChange);
            }
            form.addEventListener('submit', () => {
                if (submitButton) {
                    submitButton.dataset.caseAnalysisOriginalText ||= submitButton.value || submitButton.textContent || "Start analysis";
                    submitButton.disabled = true;
                    if ("value" in submitButton) submitButton.value = "Starting...";
                    submitButton.textContent = "Starting...";
                }

                const modalElement = form.closest('.modal');
                if (!modalElement || !window.bootstrap) return;

                window.bootstrap.Modal.getOrCreateInstance(modalElement).hide();
            });
            form.addEventListener('turbo:submit-end', () => {
                if (!submitButton) return;

                submitButton.disabled = false;
                const label = submitButton.dataset.caseAnalysisOriginalText || "Start analysis";
                if ("value" in submitButton) submitButton.value = label;
                submitButton.textContent = label;
            });
        }

        if (form.dataset.caseAnalysisInitialized !== 'true') {
            form.dataset.caseAnalysisInitialized = 'true';
            updateModelFields();
            rebuildDocumentOptions();
            renderSuggestions({ forceNew: true });
        } else {
            updateDocumentSelectionCount();
        }
    });
}

function showCaseAnalysisModal() {
    if (!window.bootstrap) return;

    const modalElement = document.getElementById('caseAnalysisModal');
    const content = document.getElementById('case-analysis-modal-content');
    if (!modalElement || !content || !content.querySelector('[data-case-analysis-detail]')) return;

    window.bootstrap.Modal.getOrCreateInstance(modalElement).show();
}

function appendCaseAnalysisParam(url, key, value) {
    try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set(key, value);
        return parsed.toString();
    } catch (_error) {
        return url;
    }
}

function parseCaseAnalysisIdFromUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        const queryId = parsed.searchParams.get("analysis_id") || parsed.searchParams.get("id");
        if (/^\d+$/.test(queryId || "")) return queryId;

        const segments = parsed.pathname.split("/").filter(Boolean);
        const candidate = segments[segments.length - 1] || "";
        return /^\d+$/.test(candidate) ? candidate : "";
    } catch (_error) {
        return "";
    }
}

const CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT = "analysis";
const CASE_ANALYSIS_WORKSPACE_TAB_PARAM = "analysis_tab";
const CASE_ANALYSIS_WORKSPACE_TAB_STORAGE_PREFIX = "capo.case-analysis-workspace-tab";

function normalizeCaseAnalysisWorkspaceTab(rawValue) {
    const normalized = String(rawValue || "").toLowerCase().trim();
    if (normalized === "strategies") return "strategies";
    if (normalized === "analysis") return "analysis";
    return "";
}

function parseCaseAnalysisWorkspaceTabFromUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        return normalizeCaseAnalysisWorkspaceTab(parsed.searchParams.get(CASE_ANALYSIS_WORKSPACE_TAB_PARAM));
    } catch (_error) {
        return "";
    }
}

function caseAnalysisWorkspaceStorageKey(workspace) {
    const caseId = String(workspace?.dataset?.caseId || "").trim();
    return `${CASE_ANALYSIS_WORKSPACE_TAB_STORAGE_PREFIX}:${caseId.length ? `case-${caseId}` : "global"}`;
}

function readCaseAnalysisWorkspaceTab(workspace) {
    if (!window.localStorage || !window.localStorage.getItem) return "";

    const key = caseAnalysisWorkspaceStorageKey(workspace);
    return normalizeCaseAnalysisWorkspaceTab(window.localStorage.getItem(key));
}

function writeCaseAnalysisWorkspaceTab(workspace, tab) {
    if (!window.localStorage || !window.localStorage.setItem) return;

    const key = caseAnalysisWorkspaceStorageKey(workspace);
    const normalized = normalizeCaseAnalysisWorkspaceTab(tab);
    if (!normalized) return;

    try {
        window.localStorage.setItem(key, normalized);
    } catch (_error) {
        // Best effort only.
    }
}

function getActiveCaseAnalysisWorkspaceTabFromWorkspace(workspace) {
    if (!workspace) return "";

    const activeButton = workspace.querySelector('[data-case-analysis-workspace-tab].active');
    return normalizeCaseAnalysisWorkspaceTab(activeButton?.dataset?.caseAnalysisWorkspaceTab);
}

function resolveCaseAnalysisWorkspaceTab(workspace) {
    const requested = normalizeCaseAnalysisWorkspaceTab(workspace?.dataset?.caseAnalysisWorkspaceActiveTab);
    if (requested) return requested;

    const fromUrl = parseCaseAnalysisWorkspaceTabFromUrl(window.location.href);
    if (fromUrl) return fromUrl;

    const fromStorage = readCaseAnalysisWorkspaceTab(workspace);
    if (fromStorage) return fromStorage;

    return CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT;
}

function setCaseAnalysisWorkspaceTab(workspace, tab, persist = true) {
    if (!workspace) return;

    const normalized = normalizeCaseAnalysisWorkspaceTab(tab) || CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT;
    const buttons = workspace.querySelectorAll('[data-case-analysis-workspace-tab]');

    buttons.forEach((button) => {
        const tabName = normalizeCaseAnalysisWorkspaceTab(button.dataset.caseAnalysisWorkspaceTab);
        const isActive = tabName === normalized;
        button.classList.toggle("active", isActive);
        button.setAttribute("aria-selected", isActive ? "true" : "false");

        const paneId = button.getAttribute("data-bs-target");
        if (!paneId || !paneId.startsWith("#")) return;

        const pane = workspace.querySelector(paneId);
        if (!pane || !pane.classList) return;
        pane.classList.toggle("show", isActive);
        pane.classList.toggle("active", isActive);
    });

    if (persist) {
        writeCaseAnalysisWorkspaceTab(workspace, normalized);
        persistCaseAnalysisSelectionInUrl(window.location.href, window.location.href);
    }
}

function initializeCaseAnalysisWorkspaceTabs(root = document) {
    if (!root) return;

    const workspaces = root.matches?.('[data-case-analysis-workspace="true"]')
        ? [root]
        : Array.from(root.querySelectorAll?.('[data-case-analysis-workspace="true"]') || []);

    workspaces.forEach((workspace) => {
        const desiredTab = resolveCaseAnalysisWorkspaceTab(workspace);
        setCaseAnalysisWorkspaceTab(workspace, desiredTab, true);
    });
}

function persistCaseAnalysisSelectionInUrl(url, fallbackUrl) {
    const analysisId = parseCaseAnalysisIdFromUrl(url);
    const baseUrl = new URL(fallbackUrl, window.location.origin);
    const workspace = document.querySelector('[data-case-analysis-workspace="true"]');
    const activeTab = getActiveCaseAnalysisWorkspaceTabFromWorkspace(workspace)
        || parseCaseAnalysisWorkspaceTabFromUrl(window.location.href)
        || CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT;

    if (analysisId) {
        baseUrl.searchParams.set("analysis_id", analysisId);
    } else {
        baseUrl.searchParams.delete("analysis_id");
    }

    if (activeTab === CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT) {
        baseUrl.searchParams.delete(CASE_ANALYSIS_WORKSPACE_TAB_PARAM);
    } else {
        baseUrl.searchParams.set(CASE_ANALYSIS_WORKSPACE_TAB_PARAM, activeTab);
    }

    if (window.history && typeof window.history.replaceState === "function") {
        window.history.replaceState({}, "", baseUrl.toString());
    }
}

function clearCaseAnalysisPanelLoadError() {
    document.getElementById("case-analysis-panel-load-error")?.remove();
}

function setCaseAnalysisPanelLoading(loading, liveTarget, rightTarget) {
    document.getElementById("case-analysis-panel-loading")?.remove();
    [liveTarget, rightTarget].filter(Boolean).forEach((target) => {
        if (loading) target.setAttribute("aria-busy", "true");
        else target.removeAttribute("aria-busy");
    });
    const target = liveTarget || rightTarget;
    if (!loading || !target?.parentElement) return;

    const notice = document.createElement("div");
    notice.id = "case-analysis-panel-loading";
    notice.className = "small text-body-secondary border-bottom py-2 px-3 flex-shrink-0";
    notice.setAttribute("role", "status");
    notice.textContent = "Loading analysis…";
    target.before(notice);
}

function cancelCaseAnalysisPanelLoad() {
    const state = window.caseAnalysisPanelLoadRequest;
    if (!state?.controller) return;
    state.sequence += 1;
    state.controller.abort();
    state.controller = null;
    setCaseAnalysisPanelLoading(false,
        document.getElementById("case-document-analysis-workspace-live"),
        document.getElementById("case-document-analysis-right-column-content"));
}

function showCaseAnalysisPanelLoadError(message, liveTarget, rightTarget) {
    clearCaseAnalysisPanelLoadError();
    const target = liveTarget || rightTarget;
    if (!target?.parentElement) return;

    const notice = document.createElement("div");
    notice.id = "case-analysis-panel-load-error";
    notice.className = "alert alert-warning py-2 px-3 m-2 flex-shrink-0";
    notice.setAttribute("role", "alert");
    notice.style.maxHeight = "5rem";
    notice.style.overflowY = "auto";
    notice.textContent = message;
    target.before(notice);
}

function clearUnavailableCaseAnalysisSelection(url) {
    const missingId = parseCaseAnalysisIdFromUrl(url);
    if (!missingId) return;

    const current = new URL(window.location.href);
    if (current.searchParams.get("analysis_id") !== missingId) return;
    current.searchParams.delete("analysis_id");
    window.history?.replaceState?.({}, "", current.toString());
}

async function loadCaseAnalysisPanel(url) {
    const liveTarget = document.getElementById("case-document-analysis-workspace-live");
    const rightTarget = document.getElementById("case-document-analysis-right-column-content");
    if (!liveTarget && !rightTarget) return false;

    const state = window.caseAnalysisPanelLoadRequest ||= { sequence: 0, controller: null };
    const sequence = ++state.sequence;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    const currentWorkspace = () => sequence === state.sequence &&
        (!liveTarget || document.getElementById(liveTarget.id) === liveTarget) &&
        (!rightTarget || document.getElementById(rightTarget.id) === rightTarget);
    const currentRequest = () => currentWorkspace() && !controller.signal.aborted;
    clearCaseAnalysisPanelLoadError();
    setCaseAnalysisPanelLoading(true, liveTarget, rightTarget);
    let timedOut = false;
    const timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, 30000);

    try {
        const response = await fetch(appendCaseAnalysisParam(url, 'panel', '1'), {
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
            signal: controller.signal
        });
        if (!currentRequest()) return false;
        if (!response.ok) {
            if (response.status === 404) {
                clearUnavailableCaseAnalysisSelection(url);
                document.dispatchEvent(new CustomEvent("data-tables:refresh", {
                    detail: { table_id: "case-analysis-history-table", analysis_id: parseCaseAnalysisIdFromUrl(url) }
                }));
            }
            showCaseAnalysisPanelLoadError(
                response.status === 404
                    ? "That analysis is unavailable in this case. The current analysis remains open."
                    : "Unable to open that analysis. The current analysis remains open; try again.",
                liveTarget, rightTarget
            );
            return false;
        }

        const html = await response.text();
        if (!currentRequest()) return false;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const panelMain = parsed.querySelector('[data-case-analysis-panel-main]');
        const panelRight = parsed.querySelector('[data-case-analysis-panel-right]');
        const panelMainPayload = panelMain?.querySelector("[data-case-analysis-panel-main-content]") || panelMain;
        const panelRightPayload = panelRight?.querySelector("[data-case-analysis-panel-right-content]") || panelRight;
        if ((liveTarget && !panelMainPayload) || (rightTarget && !panelRightPayload)) {
            showCaseAnalysisPanelLoadError("Unable to open that analysis. The current analysis remains open; try again.", liveTarget, rightTarget);
            return false;
        }

        if (liveTarget) liveTarget.replaceChildren(...Array.from(panelMainPayload.childNodes));
        if (rightTarget) rightTarget.replaceChildren(...Array.from(panelRightPayload.childNodes));
        persistCaseAnalysisSelectionInUrl(url, window.location.href);
        return true;
    } catch (error) {
        if (timedOut && currentWorkspace()) {
            showCaseAnalysisPanelLoadError("Opening this analysis took too long. The current analysis remains open; select the record again to retry.", liveTarget, rightTarget);
            return false;
        }
        if (!currentRequest() || error?.name === "AbortError") return false;
        showCaseAnalysisPanelLoadError("Unable to open that analysis. Check your connection and try again; the current analysis remains open.", liveTarget, rightTarget);
        return false;
    } finally {
        window.clearTimeout(timeout);
        if (sequence === state.sequence) {
            state.controller = null;
            setCaseAnalysisPanelLoading(false, liveTarget, rightTarget);
        }
    }
}

async function loadCaseAnalysisModal(url) {
    const target = document.getElementById('case-analysis-modal-content');
    if (!target) return;

    const response = await fetch(appendCaseAnalysisParam(url, 'modal', '1'), {
        headers: {
            Accept: 'text/html'
        },
        credentials: 'same-origin'
    });
    if (!response.ok) return;

    target.innerHTML = await response.text();
    showCaseAnalysisModal();
}

function escapeHTML(value) {
    const element = document.createElement("div");
    element.textContent = value == null ? "" : String(value);
    return element.innerHTML;
}

function normalizedCaseWorkspacePath(value) {
    if (!value) return "";

    try {
        const url = new URL(value, window.location.origin);
        return url.pathname.replace(/\/+$/, "") || "/";
    } catch (_error) {
        return value.toString().split("?")[0].replace(/\/+$/, "") || "/";
    }
}

// The view a path is in stays lit, including nested pages (an open research
// lights Legal Research): the link whose path is the longest prefix wins. On
// mobile the lit tab is scrolled into the tab strip's view.
function setActiveCaseWorkspaceLink(pathOrUrl) {
    const selectedPath = normalizedCaseWorkspacePath(pathOrUrl);
    if (!selectedPath) return;

    const links = Array.from(document.querySelectorAll('[data-case-workspace-link]'));
    const pathOf = (link) => normalizedCaseWorkspacePath(link.dataset.caseWorkspacePath || link.getAttribute('href'));
    const best = links
        .filter((link) => {
            const linkPath = pathOf(link);
            return linkPath && (selectedPath === linkPath || selectedPath.startsWith(`${linkPath}/`));
        })
        .sort((a, b) => pathOf(b).length - pathOf(a).length)[0];
    if (!best) return;

    links.forEach((link) => {
        const isActive = link === best || pathOf(link) === pathOf(best);
        link.classList.toggle('active', isActive);
        if (isActive) {
            link.setAttribute('aria-current', 'page');
        } else {
            link.removeAttribute('aria-current');
        }
    });

    const strip = best.closest('nav');
    if (strip && strip.scrollWidth > strip.clientWidth) {
        const offset = best.getBoundingClientRect().left - strip.getBoundingClientRect().left;
        strip.scrollLeft += offset - (strip.clientWidth - best.offsetWidth) / 2;
    }
}

function initializeCaseWorkspaceNavigation(root = document) {
    const currentLink = root.querySelector('[data-case-workspace-link].active') ||
        document.querySelector('[data-case-workspace-link].active') ||
        root.querySelector('[data-case-workspace-link]');

    if (currentLink) setActiveCaseWorkspaceLink(currentLink.dataset.caseWorkspacePath || currentLink.getAttribute('href'));
}

// Every case view reopens after a refresh: when the workspace (or a view
// frame inside it, like an open legal research) navigates, the page address
// records that view as ?view=. An analysis keeps its own analysis_id address.
function rememberCaseWorkspaceView(src) {
    if (!src) return;
    let view;
    try { view = new URL(src, window.location.origin); } catch (_error) { return; }
    const match = view.pathname.match(/^\/cases\/([^/]+)\//);
    if (!match || view.origin !== window.location.origin) return;
    const page = new URL(window.location.href);
    if (!new RegExp(`^/cases/${match[1]}/?$`).test(page.pathname)) return;

    const analyses = /\/case_document_analyses(?:\/|$)/.test(view.pathname);
    const defaultView = view.pathname === `/cases/${match[1]}/case_documents` && !view.search;
    if (!analyses) {
        page.searchParams.delete("analysis_id");
        page.searchParams.delete(CASE_ANALYSIS_WORKSPACE_TAB_PARAM);
    }
    page.searchParams.delete("workspace");
    if (defaultView || (analyses && page.searchParams.has("analysis_id"))) {
        page.searchParams.delete("view");
    } else {
        page.searchParams.set("view", `${view.pathname}${view.search}`);
    }
    const next = `${page.pathname}${page.search}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
        window.history.replaceState(window.history.state, "", next);
    }
}

document.addEventListener('turbo:frame-load', (event) => {
    const frame = event.target;
    if (!frame || !(frame.id === 'case_workspace_frame' || frame.hasAttribute('data-case-workspace-view-frame'))) return;
    const src = frame.getAttribute('src') || frame.src;
    rememberCaseWorkspaceView(src);
    // The view loaded (or a view nested in it, like an open research) stays lit.
    if (src) setActiveCaseWorkspaceLink(src);
});

function caseWorkspaceShellPathFor(pathOrUrl) {
    const selectedPath = normalizedCaseWorkspacePath(pathOrUrl);
    const match = selectedPath.match(/^\/cases\/([^/]+)/);
    if (!match) return "";

    const url = new URL(`/cases/${match[1]}`, window.location.origin);
    if (selectedPath.endsWith("/case_documents/batch_upload")) {
        url.searchParams.set("workspace", "batch_upload");
    } else if (!/\/case_document_analyses(?:\/|$)/.test(selectedPath) && selectedPath !== `/cases/${match[1]}/case_documents`) {
        url.searchParams.set("view", selectedPath);
    }
    const currentAnalysisId = (() => {
        try {
            const current = new URL(window.location.href);
            const analysisId = current.searchParams.get("analysis_id");
            return analysisId && /^\d+$/.test(analysisId) ? analysisId : "";
        } catch (_error) {
            return "";
        }
    })();
    const currentTab = (() => {
        if (!/\/case_document_analyses(?:\/|$)/.test(selectedPath)) return "";
        const workspace = document.querySelector('[data-case-analysis-workspace="true"]');
        const byDataset = normalizeCaseAnalysisWorkspaceTab(workspace?.dataset?.caseAnalysisWorkspaceActiveTab);
        return (
            parseCaseAnalysisWorkspaceTabFromUrl(window.location.href) ||
            byDataset ||
            CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT
        );
    })();

    if (currentAnalysisId) {
        url.searchParams.set("analysis_id", currentAnalysisId);
    }
    if (currentTab === CASE_ANALYSIS_WORKSPACE_TAB_DEFAULT) {
        url.searchParams.delete(CASE_ANALYSIS_WORKSPACE_TAB_PARAM);
    } else if (currentTab) {
        url.searchParams.set(CASE_ANALYSIS_WORKSPACE_TAB_PARAM, currentTab);
    }

    return `${url.pathname}${url.search}`;
}

if (!window.caseDocumentAssociationBindingsAdded) {
    window.caseDocumentAssociationBindingsAdded = true;

    document.addEventListener('turbo:load', () => {
        initializeCaseDocumentAssociationForms(document);
        initializeTagSelects(document);
        initializeBatchIngestForms(document);
        initializeCaseDocumentModals(document);
        initializeCaseDocumentList(document);
        initializeCaseAnalysisForms(document);
        initializeChat(document);
        initializeCaseWorkspaceNavigation(document);
        initializeCaseAnalysisWorkspaceTabs(document);
        bindAssociationReviewPage(document);
        applyDataDrivenStyles(document);
    });

    document.addEventListener('turbo:submit-start', (event) => {
        const form = event.target.closest('[data-dismiss-modal-on-submit="true"]');
        if (!form || !window.bootstrap) return;

        const modalElement = form.closest('.modal');
        if (!modalElement) return;

        window.bootstrap.Modal.getOrCreateInstance(modalElement).hide();
    });

    document.addEventListener('turbo:frame-missing', (event) => {
        const frame = event.target;
        if (!frame || frame.id !== 'case_workspace_frame' || !window.Turbo) return;

        const selectedPath = frame.dataset.caseWorkspaceSelectedPath || window.location.pathname;
        const shellPath = caseWorkspaceShellPathFor(selectedPath);
        if (!shellPath) return;

        event.preventDefault();
        window.Turbo.visit(shellPath, { action: 'replace' });
    });

    document.addEventListener('click', (event) => {
        const documentPreviewLink = event.target.closest('[data-document-preview-url]');
        if (documentPreviewLink) {
            const frame = document.getElementById('documentIframe');
            const placeholder = document.getElementById('iframePlaceholder');
            if (frame) {
                event.preventDefault();
                frame.src = documentPreviewLink.dataset.documentPreviewUrl;
                frame.classList.remove('d-none');
                placeholder?.classList.add('d-none');
            }
            return;
        }

        const chatTrigger = event.target.closest('[data-chat-offcanvas-toggle]');
        if (chatTrigger) {
            event.preventDefault();
            const offcanvas = chatOffcanvasElement(document);
            if (offcanvas && window.bootstrap && window.bootstrap.Offcanvas) {
                const opening = !offcanvas.classList.contains('show');
                window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).toggle();
                if (opening) {
                    window.requestAnimationFrame(() => {
                        scrollChatToBottom(document);
                        focusChatInput(document);
                    });
                }
            }
            return;
        }

        const caseWorkspaceLink = event.target.closest('[data-case-workspace-link]');
        if (caseWorkspaceLink) {
            if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

            const targetPath = caseWorkspaceLink.dataset.caseWorkspacePath || caseWorkspaceLink.getAttribute('href');
            setActiveCaseWorkspaceLink(targetPath);

            const frameId = caseWorkspaceLink.dataset.turboFrame;
            const frame = frameId ? document.getElementById(frameId) : null;
            if (frame) frame.dataset.caseWorkspaceSelectedPath = targetPath;
        }

        const remoteModalTrigger = event.target.closest('[data-remote-modal-url]');
        if (remoteModalTrigger) {
            event.preventDefault();
            openRemoteContentModal(remoteModalTrigger);
            return;
        }

        const documentModalTrigger = event.target.closest('[data-case-document-document-url]');
        if (documentModalTrigger) {
            event.preventDefault();
            openCaseDocumentDocumentModal(documentModalTrigger);
            return;
        }

        const historyLink = event.target.closest('[data-case-analysis-history-link]');
        if (!historyLink) return;

        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (historyLink.target === '_blank') return;
        if (event.button && event.button !== 0) return;

        event.preventDefault();

        loadCaseAnalysisPanel(historyLink.href);
    });

    document.addEventListener('shown.bs.modal', (event) => {
        initializeCaseDocumentAssociationForms(event.target);
        initializeCaseAnalysisForms(event.target);
        initializeGenericSelect2(event.target);
        initializeTagSelects(event.target);
        bindAssociationReviewPage(event.target);

        const uploadInput = event.target.querySelector('[data-upload-file-input]');
        if (uploadInput) uploadInput.focus();

        const previewFrame = event.target.querySelector('[data-preview-src]');
        if (previewFrame && !previewFrame.src) {
            previewFrame.src = previewFrame.dataset.previewSrc;
        }
    });

    document.addEventListener('hidden.bs.modal', (event) => {
        const previewFrame = event.target.querySelector('[data-preview-src]');
        if (previewFrame) previewFrame.removeAttribute('src');
    });

    document.addEventListener('shown.bs.tab', (event) => {
        const tabButton = event.target.closest('[data-case-analysis-workspace-tab]');
        if (!tabButton) return;

        const workspace = tabButton.closest('[data-case-analysis-workspace="true"]');
        const tab = normalizeCaseAnalysisWorkspaceTab(tabButton.dataset?.caseAnalysisWorkspaceTab);
        if (!workspace || !tab) return;

        setCaseAnalysisWorkspaceTab(workspace, tab, true);
    });

    // A sent message shows at once (pending) and the box clears; the server's
    // copy, in the response, takes its place. A failed send puts the text back.
    document.addEventListener('turbo:submit-start', (event) => {
        const form = event.target.closest('#message-form');
        if (!form) return;
        const input = form.querySelector('#chat-text');
        const text = (input?.value || '').trim();
        const list = document.getElementById('messages');
        if (!text || !list) return;

        const row = document.createElement('div');
        row.className = 'd-flex chat-message-row justify-content-end is-chat-current-user chat-message-pending';
        const content = document.createElement('div');
        content.className = 'chat-message-content text-end';
        const bubble = document.createElement('div');
        bubble.className = 'rounded-3 px-3 py-2 text-break chat-message-bubble text-bg-primary';
        bubble.textContent = text;
        content.append(bubble);
        row.append(content);
        list.append(row);
        form.dataset.pendingText = text;
        if (input) input.value = '';
        scrollChatToBottom(document);
    });

    document.addEventListener('turbo:submit-end', (event) => {
        const form = event.target.closest('#message-form');
        if (!form) return;
        document.querySelectorAll('#messages .chat-message-pending').forEach((row) => row.remove());
        if (event.detail && event.detail.success === false) {
            const input = form.querySelector('#chat-text');
            if (input && !input.value) input.value = form.dataset.pendingText || '';
            return;
        }

        form.reset();
        window.requestAnimationFrame(() => {
            scrollChatToBottom(document);
            focusChatInput(document);
        });
    });

    document.addEventListener('submit', (event) => {
        const form = event.target.closest('#message-form');
        if (!form || window.Turbo) return;

        window.setTimeout(() => {
            form.reset();
            scrollChatToBottom(document);
            focusChatInput(document);
        }, 0);
    });

    document.addEventListener('turbo:before-cache', () => {
        cancelCaseAnalysisPanelLoad();
        destroyBootstrapSelect2(document);
        const offcanvas = chatOffcanvasElement(document);
        if (!offcanvas || !window.bootstrap || !window.bootstrap.Offcanvas) return;

        const instance = window.bootstrap.Offcanvas.getInstance(offcanvas);
        if (instance) instance.hide();
    });

    document.addEventListener('turbo:frame-load', (event) => {
        initializeChat(event.target);
        initializeGenericSelect2(event.target);
        initializeTagSelects(event.target);
        initializeCaseAnalysisForms(event.target);
        initializeBatchIngestForms(event.target);
        bindAssociationReviewPage(event.target);
        initializeCaseAnalysisWorkspaceTabs(event.target);
        applyDataDrivenStyles(event.target);
        if (event.target.id === 'case_workspace_frame') {
            initializeCaseWorkspaceNavigation(document);
        }
    });

    document.addEventListener('turbo:before-stream-render', (event) => {
        const originalRender = event.detail?.render;
        if (typeof originalRender !== 'function') return;

        event.detail.render = async (streamElement) => {
            const action = String(streamElement.getAttribute('action') || 'append');
            const insertsHtml = ['append', 'prepend', 'replace', 'update', 'before', 'after'].includes(action);
            const targetId = streamElement.getAttribute('target');
            const target = targetId ? document.getElementById(targetId) : null;
            if (!caseAnalysisSourceListUpdateAllowed(streamElement, target)) return;

            await originalRender(streamElement);
            // Custom progress/token actions mutate existing text or dispatch an event. They
            // insert no widgets, so rescanning document here recreated every Select2 on every
            // analysis snapshot and saturated the browser's main thread.
            if (!insertsHtml || target?.matches?.('[data-case-analysis-stream-target]')) return;

            window.requestAnimationFrame(() => {
                syncCaseAnalysisSourceListTarget(streamElement, target);
                // `replace` detaches the target captured before rendering. Reacquire
                // the live node so non-Stimulus widgets initialize inside the new pane.
                const root = (targetId && document.getElementById(targetId)) || target;
                if (!root) return;

                initializeTagSelects(root);
                initializeGenericSelect2(root);
                initializeCaseAnalysisForms(root);
                initializeBatchIngestForms(root);
                bindAssociationReviewPage(root);
                applyDataDrivenStyles(root);
            });
        };
    });

    document.addEventListener('data-tables:draw', (event) => {
        bindAssociationReviewPage(event.detail?.element || document);
    });

    document.addEventListener('turbo:render', () => {
        initializeChat(document);
        initializeGenericSelect2(document);
        initializeTagSelects(document);
        initializeCaseAnalysisForms(document);
        initializeBatchIngestForms(document);
        initializeCaseAnalysisWorkspaceTabs(document);
        bindAssociationReviewPage(document);
        applyDataDrivenStyles(document);
    });
}

if (!window.caseDocumentBindingsAdded) {
    window.caseDocumentBindingsAdded = true;

    const initializeDocumentSurface = () => {
        initializeTagSelects(document);
        initializeGenericSelect2(document);
        initializeBatchIngestForms(document);
        initializeCaseDocumentModals(document);
        initializeCaseDocumentList(document);
        initializeCaseAnalysisForms(document);
        initializeChat(document);
        initializeCaseWorkspaceNavigation(document);
        initializeCaseAnalysisWorkspaceTabs(document);
        bindAssociationReviewPage(document);
        applyDataDrivenStyles(document);
    };

    document.addEventListener('DOMContentLoaded', initializeDocumentSurface);
    if (document.readyState !== 'loading') {
        window.requestAnimationFrame(initializeDocumentSurface);
    }

    document.addEventListener('submit', async (event) => {
        const form = event.target.closest('[data-copy-form]');
        if (!form) return;

        event.preventDefault();

        const input = form.querySelector('[data-copy-input]');
        const button = form.querySelector('[data-copy-button]');
        if (!input) return;

        input.select();
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(input.value);
            } else {
                document.execCommand('copy');
            }
        } catch (_error) {
            document.execCommand('copy');
        }

        if (button) {
            button.textContent = 'Copied';
            setTimeout(() => {
                button.textContent = 'Copy';
            }, 1400);
        }
    });
}

if (!window.dataDrivenStyleObserverAdded && typeof MutationObserver !== 'undefined') {
    window.dataDrivenStyleObserverAdded = true;

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) applyDataDrivenStyles(node);
            });
        });
    }).observe(document.documentElement, {
        childList: true,
        subtree: true
    });
}
